import { LoadingPage } from "$components/loading/loading-page";
import { DbErrorPage } from "$components/page/db-error-page";
import {
  type Patient,
  RXDB_PATIENT_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/patient";
import {
  RxDBInitialisationError,
  useRxDBContext,
} from "$global-providers/rxdb-provider/provider";
import { useUser } from "$global-providers/user-provider";
import { isDemoOrg } from "$routes/_lib/is-demo-org";
import { linkToHome } from "$routes/_lib/links";
import { useQuerystring } from "$routes/_lib/use-querystring";
import { useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useRxData } from "rxdb-hooks";
import { ClinicalDecisionSupportProvider } from "./_lib/clinical-decision-support-service/provider";
import { VaccinationsProvider } from "./_lib/vaccinations-service/provider";

export const PatientDetailsLayout = ({
  children = <Outlet />,
}: WithChildren) => {
  const query = useQuerystring();
  const navigate = useNavigate();
  const {
    initialisationError,
    replicationState: {
      patient: { isPulling: isPullingPatient },
    },
  } = useRxDBContext();
  const user = useUser();

  const patientId = query.patientUuid || query.patient;

  const { result: patientData, isFetching: isFetchingPatientFromRxDb } =
    useRxData<Patient>(RXDB_PATIENT_COLLECTION_NAME, (collection) =>
      collection.findOne({
        selector: { id: patientId },
      }),
    );
  const patient = patientData[0];
  const isLoading = isFetchingPatientFromRxDb || isPullingPatient;

  const isDemoUserViewingNonDemoPatient =
    !!user?.organisationId &&
    isDemoOrg(user.organisationId) &&
    !!patient?.organisationId &&
    !isDemoOrg(patient.organisationId);

  useEffect(() => {
    if (
      !patientId ||
      (!isLoading && !patient) ||
      isDemoUserViewingNonDemoPatient
    ) {
      navigate(linkToHome());
    }
  }, [
    patientId,
    navigate,
    patient,
    isLoading,
    isDemoUserViewingNonDemoPatient,
  ]);

  if (initialisationError) {
    return (
      <DbErrorPage
        isPatientPage
        isRecoverable={
          initialisationError === RxDBInitialisationError.RECOVERABLE
        }
      />
    );
  }

  if (!patient) {
    return <LoadingPage />;
  }

  return (
    <VaccinationsProvider patientId={patientId}>
      <ClinicalDecisionSupportProvider patientId={patientId}>
        {children}
      </ClinicalDecisionSupportProvider>
    </VaccinationsProvider>
  );
};
